import {BulkSplitViewFilter, SplitViewFilter} from '@webmate-web-core/shared/components/split-view/interfaces/split-view.interface';
import {ChangeDetectionStrategy, Component, forwardRef, Input} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {InputTextModule} from 'primeng/inputtext';
import {
    AdvancedSortingConfig,
    FilterConfig,
    FilterOption,
    FilterOptionName,
    FixedSortingConfig,
    SearchFilterFunction,
    SimpleSortingConfig,
    SortingConfig,
    SortingOption,
    SortingOptionName
} from '@webmate-web-core/shared/components/split-view/interfaces/sorting-filtering.interface';
import {NgClass} from '@angular/common';
import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {PopoverModule} from 'primeng/popover';
import {SharedModule} from 'primeng/api';
import {CheckboxModule} from 'primeng/checkbox';
import {SelectButtonModule} from 'primeng/selectbutton';
import {IconProp} from '@fortawesome/fontawesome-svg-core';

export interface ActionConfig {
    icon: IconProp;
    action: () => void;
}

@Component({
    selector: 'wm-split-view-generic-filter',
    templateUrl: 'generic-filter.component.html',
    styleUrls: ['generic-filter.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
    FormsModule,
    InputTextModule,
    FontAwesomeModule,
    PopoverModule,
    SharedModule,
    NgClass,
    CheckboxModule,
    SelectButtonModule
],
    providers: [{provide: SplitViewFilter, useExisting: forwardRef(() => GenericFilterComponent)}]
})
export class GenericFilterComponent<T> extends BulkSplitViewFilter<T> {

    @Input() searchFilterFunction: SearchFilterFunction<T> = (e, filterString) => JSON.stringify(e).includes(filterString);

    @Input() additionalFilterConfig: FilterConfig<T>;

    @Input() sortingConfig: SortingConfig;

    @Input() additionalActions?: ActionConfig[];

    @Input() disabled: boolean = false;

    private _entities: T[];

    set entities(e: T[]) {
        this._entities = e;
        this.applyFilter();
    }

    get entities() {
        return this._entities;
    }

    filterString = '';
    selectedSortingOption: SortingOptionName;
    selectedFilterOptions: FilterOptionName[] = [];

    bulkModeState: boolean = false;

    constructor() {
        super();
    }

    ngOnInit() {
        if (this.sortingConfig?.type === 'advanced') {
            this.selectedSortingOption = (this.sortingConfig as AdvancedSortingConfig<T>).defaultSortingOption;
        }
    }


    private getSortingFunction() {
        switch (this.sortingConfig.type) {
            case 'simple':
                return (a: T, b: T) => {
                    return (a[(this.sortingConfig as SimpleSortingConfig<T>).sortingKey] as string).localeCompare(b[(this.sortingConfig as SimpleSortingConfig<T>).sortingKey] as string)
                }
            case 'fixed':
                return (this.sortingConfig as FixedSortingConfig<T>).sortingFunction;
            case 'advanced':
                return (this.sortingConfig as AdvancedSortingConfig<T>).sortingOptions.find(s => s.id === this.selectedSortingOption)!.sortingFunction;
        }
    }

    applyFilter() {
        let entities = [...this._entities];
        if (this.sortingConfig) {
            entities.sort(this.getSortingFunction());
        }

        if (!!this.filterString) {
            entities = entities.filter(e => this.searchFilterFunction(e, this.filterString));
        }

        if (this.additionalFilterConfig?.filterMatching === 'any') {
            // Calculate union of all filter results
            if (this.selectedFilterOptions.length > 0) {
                let filtered: T[] = [];
                this.selectedFilterOptions.forEach(id => {
                   filtered.push(...entities.filter(this.getFilterOptionById(id).filterFunction));
                });
                entities = filtered;
            }
        } else if (this.additionalFilterConfig?.filterMatching === 'all') {
            // Apply all filters incrementally
            this.selectedFilterOptions.forEach(id => {
                entities = entities.filter(this.getFilterOptionById(id).filterFunction);
            });
        }

        this.entitiesChanged.emit(entities);
    }

    getSortingOptions() {
        if (this.sortingConfig?.type !== 'advanced') {
            throw new Error("Sorting Options only for advanced sorting");
        }
        return (this.sortingConfig as AdvancedSortingConfig<T>).sortingOptions;
    }

    selectSortingOption(s: SortingOption<T>) {
        if (s.id !== this.selectedSortingOption) {
            this.selectedSortingOption = s.id;
            this.applyFilter();
        }
    }

    getFilterOptionById(id: FilterOptionName): FilterOption<T> {
        return this.additionalFilterConfig.filterOptions.find(f => f.id === id)!;
    }

    toggleFilterOption(id: FilterOptionName) {
        if (this.selectedFilterOptions.includes(id)) {
            this.selectedFilterOptions.splice(this.selectedFilterOptions.indexOf(id), 1);
        } else {
            this.selectedFilterOptions.push(id);
        }
        this.applyFilter();
    }

    toggleBulkMode() {
        if (!this.bulkModeConfig) {
            return;
        }
        this.bulkModeState = !this.bulkModeState;
        this.bulkModeConfig?.bulkModeToggleCallback(this.bulkModeState);
    }

    selectAllClicked(event: boolean) {
        this.bulkModeConfig?.toggleAllCallback(event);
    }

}
